import type { ReactNode } from "react"

import { DashboardHeader } from "@/components/dashboard/header"
import { Sidebar } from "@/components/dashboard/sidebar"

/**
 * Authenticated app shell.
 *
 * The shell shares the public layout's editorial ground: an Oat canvas
 * (`bg-surface-base`) under the mesh-wash + grain atmosphere borrowed from
 * `public-layout.tsx` / `/design`. The dashboard reads as the public
 * experience's quieter sibling rather than a white admin pane — white content
 * cards lift off the cream, and the Sidebar is the Manila rail beside it.
 *
 * The atmosphere is mounted on a dedicated, isolated background layer rather
 * than by putting `.app-canvas` on this flex parent. `.app-canvas > *` forces
 * every direct child to `position: relative` (it is what silently de-stickies
 * the public header), which would break the Sidebar's `lg:fixed` rail and
 * flatten the slideout's `z-50` overlay. The empty `.app-canvas` host below
 * carries only the grain pseudo-elements, parked behind content at `-z-10`; the
 * isolate keeps that negative layer above the Oat base and below everything
 * else without touching the Sidebar's own stacking. The content column's
 * sticky `z-30` DashboardHeader relies on the same arrangement — it stays
 * sticky only while `.app-canvas` remains a sibling, not a parent.
 */
export const DashboardLayout = ({ children }: { children: ReactNode }) => {
  return (
    <div className="bg-surface-base relative isolate flex min-h-screen flex-col lg:flex-row">
      {/* Keyboard users skip the rail + account menu straight to the page. */}
      <a
        href="#main-content"
        className="bg-primary text-primary outline-focus-ring sr-only z-40 rounded-lg px-4 py-2 text-sm font-semibold shadow-xs focus-visible:not-sr-only focus-visible:absolute focus-visible:top-2 focus-visible:left-2 focus-visible:outline-2"
      >
        Skip to content
      </a>

      {/* SVG turbulence filter consumed by `.app-canvas::after` (must be in the DOM). */}
      <svg width="0" height="0" className="absolute" aria-hidden="true">
        <filter id="canvas-grain">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.65"
            numOctaves={3}
            stitchTiles="stitch"
          />
        </filter>
      </svg>
      <div
        aria-hidden="true"
        className="app-canvas pointer-events-none -z-10"
      />

      <Sidebar />

      <div className="flex min-w-0 flex-1 flex-col">
        <DashboardHeader />
        <main id="main-content" className="min-w-0 flex-1">
          {children}
        </main>
      </div>
    </div>
  )
}
